import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CircleCheck as CheckCircle, ArrowRight, Phone, Sparkles, Target, CircleAlert as AlertCircle, Clock, Shield, Settings, Users, TrendingUp, Zap } from 'lucide-react';

const InterimsITLeiter = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Interims IT-Leiter Mönchengladbach',
    description: 'Interims IT-Leitung für KMU: schnelle Übernahme, Krisenmanagement, Stabilisierung. Sofort verfügbar im Raum Mönchengladbach, Viersen, Krefeld.',
    provider: { '@type': 'LocalBusiness', name: 'Schteffann', url: 'https://schteffann.de/' },
    areaServed: ['Mönchengladbach', 'Viersen', 'Krefeld', 'Neuss', 'Düsseldorf', 'Nordrhein-Westfalen'],
    serviceType: 'Interim Management'
  };

  const szenarien = [
    { icon: AlertCircle, text: 'Plötzlicher Ausfall des IT-Leiters' },
    { icon: Users, text: 'Kündigung oder Krankheit' },
    { icon: Clock, text: 'Übergangsphasen bis zur Neubesetzung' },
    { icon: Shield, text: 'Krisen oder Sicherheitsvorfälle' },
    { icon: Settings, text: 'Projektübernahmen' },
    { icon: TrendingUp, text: 'Professionalisierung der IT-Strukturen' }
  ];

  const aufgaben = [
    'Sofortige Übernahme der Führungsverantwortung',
    'Stabilisierung der IT-Betriebsprozesse',
    'Priorisierung kritischer Aufgaben',
    'Krisenmanagement',
    'Kommunikation mit Geschäftsführung & Mitarbeitern',
    'Übergabe an den zukünftigen IT-Leiter'
  ];

  const relatedPages = [
    { title: 'IT-Leiter für KMU', url: '/it-leiter-kmu', description: 'Langfristige strategische IT-Führung' },
    { title: 'IT-Bereichsleiter', url: '/it-bereichsleiter', description: 'Führung & Struktur für Ihre IT-Abteilung' },
    { title: 'Interims IT-Administrator', url: '/interims-it-administrator', description: 'Schnelle technische Unterstützung' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="Interims IT-Leiter Mönchengladbach – Sofortige Unterstützung bei Ausfall oder Krise"
        description="Interims IT-Leitung für KMU in Mönchengladbach: schnelle Übernahme, Krisenmanagement, Stabilisierung. Sofort verfügbar im Raum Mönchengladbach, Viersen, Krefeld."
        keywords="Interims IT-Leiter Mönchengladbach, Interim Bereichsleiter Mönchengladbach, IT-Krisenmanagement, Übergangsmanagement IT, IT-Leiter Vertretung, Externe IT-Leitung, Interim Management, Viersen, Krefeld, NRW"
        jsonLd={seoJsonLd}
      />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-gradient-to-r from-cyan-400/15 to-blue-500/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-r from-blue-400/10 to-cyan-500/10 rounded-full blur-3xl"></div>

        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Zap className="w-4 h-4 mr-2" />
              Interims-Management
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">Interims IT-Leiter</span>
              <span className="text-slate-900"> für Unternehmen in Mönchengladbach</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Wenn Ihr IT-Leiter kurzfristig ausfällt, ein Projekt stockt oder eine Übergangsphase entsteht, übernehme ich sofort Verantwortung. Als Interims IT-Leiter bringe ich Ruhe, Struktur und klare Entscheidungen in kritischen Situationen – im Raum Mönchengladbach, Viersen und Krefeld.
            </p>
          </div>
        </div>
      </section>

      {/* Szenarien Section */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-cyan-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Clock className="w-4 h-4 mr-2" />
              Einsatzszenarien
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Typische Einsatzszenarien
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
            {szenarien.map((item, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all flex-shrink-0">
                      <item.icon className="h-6 w-6 text-cyan-600" />
                    </div>
                    <p className="text-slate-700 leading-relaxed pt-2">
                      {item.text}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Aufgaben */}
          <div className="mb-20">
            <Card className="bg-gradient-to-br from-slate-50 to-cyan-50/30 border-0 shadow-xl">
              <CardContent className="p-12 lg:p-16">
                <div className="text-center mb-12">
                  <Badge className="mb-6 bg-cyan-100 text-cyan-800 border-cyan-200 px-6 py-2">
                    <Settings className="w-4 h-4 mr-2" />
                    Aufgaben
                  </Badge>
                  <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                    Meine Aufgaben als Interims IT-Leiter
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  {aufgaben.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Related Pages */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <Badge className="mb-6 bg-cyan-100 text-cyan-800 border-cyan-200 px-6 py-2">
                <Target className="w-4 h-4 mr-2" />
                Weitere Leistungen
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Das könnte Sie auch interessieren
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPages.map((page, index) => (
                <Link key={index} to={page.url}>
                  <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-cyan-200 h-full">
                    <CardContent className="p-8">
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-cyan-700 transition-colors mb-3">
                        {page.title}
                      </h3>
                      <p className="text-slate-600 mb-4">{page.description}</p>
                      <div className="flex items-center text-cyan-600 group-hover:text-cyan-700">
                        <span className="text-sm font-medium">Mehr erfahren</span>
                        <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Sie benötigen kurzfristig Unterstützung?
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Ich bin schnell und flexibel verfügbar.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Kontakt aufnehmen
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default InterimsITLeiter;